import { PaginationSetting, ResponseObject } from './CommonModel';

export enum AlarmDetailsExpandedState {
	None = 0,
	Both,
	Notes,
	Instructions,
}

export enum AlarmState {
	Active = 0,
	Acknowledged,
	Cleared,
}

export enum AlarmDetailStatus {
	Active = 0,
	Secured = 1,
	ReturnedToNormal = 2,
}

export type AlarmNote = {
	Date: Date;
	Text: string;
	OperatorName: string;
	AlarmId: number;
	AlarmNoteId: number;
	Type: number;
};

export type AlarmDetail = {
	ActiveAlarmId: number;
	RelatedActiveAlarmId: number;
	HostTime: Date;
	HardwareTime: Date;
	Operator: string;
	Workstation: string;
	Time: Date;
	AlarmNotes: AlarmNote[];
	IsChecked: boolean;
	IsExpandableRowHeader: boolean;
	UID1: number;
	UID2: number;
	Status: AlarmDetailStatus;
};

export type AlarmModel = {
	AlarmDetails: AlarmDetail[];
	EventId: number;
	Description: string;
	Address: string;
	Level: number;
	State: AlarmState;
	Secures: number;
	ReturnedToNormal: boolean;
	Instructions: string;
	Count: number;
	AlarmSoundInfo: AlarmSoundInfo;
};

export type AlarmSoundInfo = {
	ActiveId: number;
	Path: string;
	Address: string;
	Data: string;
	ContentType: string;
};

export type AlarmListResponseObject = {
	alarmList: AlarmModel[];
	totalRecords: number;
	totalPages: number;
	pageNumber: number;
} & ResponseObject;

export type AlarmAssessmentCode = {
	Id: number;
	Name: string;
	Text: string;
};

export enum AlarmAcknowledgementActions {
	None = 0,
	AcknowledgedOne,
	AcknowledgedMultiple,
	AcknowledgedAll,
	ClearOneUnacknowledged,
	ClearMultipleUnacknowledged,
	ClearAllUnacknowledged,
	ClearOneAcknowledged,
	ClearMultipleAcknowledged,
	ClearAllAcknowledged,
}

export type AlarmSoundConfiguration = {
	MakeDelay: boolean;
	SilenceBeep: boolean;
	SilenceCustom: boolean;
	BackgroundPlay: boolean;
	DelaySeconds: number;
};

export enum AlarmAcknowledgementErrorCode {
	Success = 0,
	ActionDoesNoteMatchAlarmCount,
	AlarmAndNotesCountDoNotMatch,
	RequiresNote,
	SystemError,
}

export type AlarmAcknowledgementResults = {
	AlarmAckErrorCode: AlarmAcknowledgementErrorCode;
} & ResponseObject;

export type AcknowledgedAndClearAlarmsPayload = {
	action: AlarmAcknowledgementActions;
	alarmIds: number[];
	operatorNotes: AlarmNote[];
	alarmsDetails?: AlarmModel[];
	addOperatorNoteCallback: (action: AlarmAcknowledgementActions, payload: AcknowledgedAndClearAlarmsPayload) => AlarmNoteForAcknowledge;
	acknowledgeAndClearCallback: (payload: AcknowledgedAndClearAlarmsPayload) => AlarmNoteForAcknowledge;
	setIsLoadingCallback?: (value: boolean) => void;
	setAlarmNoteForAcknowledgeCallback: (alarmNote: AlarmNoteForAcknowledge) => void;
	setShowAlarmAddOperatorNoteCallback: () => void;
	saveChangesCallback?: () => void;
};

export type AlarmNoteForAcknowledge = {
	isAlarmNoteForAcknowledge: boolean;
	acknowledgeAction: AlarmAcknowledgementActions;
	alarms: OperatorNoteAlarm[];
};

export type OperatorNoteAlarm = Pick<AlarmModel, 'EventId' | 'Description' | 'Address' | 'Level' | 'State' | 'Secures' | 'ReturnedToNormal'> &
	Pick<AlarmDetail, 'ActiveAlarmId' | 'HostTime' | 'HardwareTime' | 'Operator' | 'Workstation' | 'Time' | 'Status'>;

export enum AlarmActionOptions {
	ADD_OPERATOR_NOTE = 10,
	DISPLAY_CREDENTIALS,
}

export type AlarmsActions = AlarmAcknowledgementActions | AlarmActionOptions;

export type AlarmsOptions = {
	key: string;
	action?: AlarmsActions;
};

export enum AlarmColorType {
	Background = 0,
	Active = 2,
	ReturnedToNormal = 3,
}

export type AlarmColorLabel = {
	ColorType: AlarmColorType;
	Label: string;
};

export enum EventSortDirection {
	Descending = -1,
	Ascending = 0,
	OperatorPreference = 1,
}

export type EventSorterConfig = {
	direction: EventSortDirection;
	field: string;
};

export type EventPaginationSetting = {
	SortDirection?: EventSortDirection;
} & Omit<PaginationSetting, 'SortDirection'>;
